/**
 * Recovering a tab that outlived its deploy.
 *
 * Hashed assets are cached forever and `index.html` never is, so a tab opened
 * before a deploy still holds the OLD entry chunk — and every lazy chunk it has
 * not fetched yet is named by an old hash. When one of those fails to load
 * (staging, 2026-09-29: `Failed to fetch dynamically imported module:
 * …/google-loader-<oldhash>.js`, the Home map stuck on "can't be shown right
 * now"), nothing in-page can recover it: the browser caches a failed dynamic
 * import in its module map, so the map's "Try again" re-imports the same dead
 * URL. A full reload is the only fix, and it is instant.
 *
 * So on the first stale-chunk failure the page reloads itself — once. The guard
 * is a timestamp in sessionStorage: if this tab already reloaded for this
 * reason inside {@link RELOAD_GUARD_MS}, a second reload would be a loop (the
 * new build is broken, or the network is), so the page instead shows an honest
 * "a new version is available" notice with a Reload button and lets the
 * operator decide.
 *
 * It also never reloads out from under somebody mid-task — the raise flow, the
 * building wizard, an open modal, or a text field they are typing in. Losing
 * half-written alert words to an automatic reload is worse than a missing map.
 * In that case the same notice shows instead.
 */

export const RELOAD_GUARD_MS = 60_000;
export const RELOAD_KEY = 'evac.stale-build-reload-at';

/**
 * Whether an error is a failed chunk load rather than a bug in the chunk. The
 * messages differ per engine: Chromium "Failed to fetch dynamically imported
 * module", Firefox "error loading dynamically imported module", Safari
 * "Importing a module script failed", and Vite's CSS preload "Unable to preload
 * CSS".
 */
export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    message,
  );
}

/**
 * Is the operator in the middle of something a reload would destroy? Cheap and
 * conservative: a false "busy" costs a banner instead of a reload; a false
 * "idle" costs somebody their typing.
 */
export function isMidTask(doc: Document = document, pathname = location.pathname): boolean {
  // The raise flow and the building wizard are whole screens of unsaved input.
  if (pathname === '/activate' || pathname === '/locations/new') return true;
  if (doc.querySelector('dialog[open], [aria-modal="true"]')) return true;
  const active = doc.activeElement;
  if (
    (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) &&
    active.value !== ''
  ) {
    return true;
  }
  if (active instanceof HTMLElement && active.isContentEditable) return true;
  return false;
}

export type StaleBuildDeps = {
  now: () => number;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload: () => void;
  busy: () => boolean;
};

let reloading = false;
let noticeShown = false;
const listeners = new Set<() => void>();

function showNotice(): void {
  if (noticeShown) return;
  noticeShown = true;
  for (const l of listeners) l();
}

/**
 * Handle one stale-chunk failure. Returns what it did, for tests. Idempotent:
 * once a reload is under way or the notice is up, later failures (a page load
 * usually fails several chunks at once) do nothing.
 */
export function handleStaleBuild(deps: StaleBuildDeps): 'reloaded' | 'notice' | 'ignored' {
  if (reloading || noticeShown) return 'ignored';

  // No storage, or storage that throws, means the guard cannot be kept — treat
  // it as "already reloaded" and never loop blind.
  let last: number | null;
  try {
    const raw = deps.storage ? deps.storage.getItem(RELOAD_KEY) : String(deps.now());
    last = raw ? Number(raw) : null;
  } catch {
    last = deps.now();
  }

  const recentlyReloaded =
    last !== null && Number.isFinite(last) && deps.now() - last < RELOAD_GUARD_MS;

  if (recentlyReloaded || deps.busy()) {
    showNotice();
    return 'notice';
  }

  try {
    deps.storage?.setItem(RELOAD_KEY, String(deps.now()));
  } catch {
    // Could not record the guard, so a reload could loop. Ask instead.
    showNotice();
    return 'notice';
  }
  reloading = true;
  deps.reload();
  return 'reloaded';
}

function browserDeps(): StaleBuildDeps {
  let storage: StaleBuildDeps['storage'];
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return {
    now: () => Date.now(),
    storage,
    reload: () => window.location.reload(),
    busy: () => isMidTask(),
  };
}

/** Report a caught error; acts only when it is a stale-chunk failure. */
export function reportChunkError(err: unknown): void {
  if (isChunkLoadError(err)) handleStaleBuild(browserDeps());
}

/**
 * Vite dispatches `vite:preloadError` on window whenever a dynamic import (or
 * one of its preloaded dependencies) fails in a built bundle — every `import()`
 * and `React.lazy` in the app goes through it, so this one listener covers them
 * all. The event is not cancelled: the original error still propagates to
 * whatever handles it (the map falls back to its panel), and the reload or the
 * notice happens beside it.
 */
export function installStaleBuildRecovery(win: Window = window): void {
  win.addEventListener('vite:preloadError', () => {
    handleStaleBuild(browserDeps());
  });
}

export function subscribeStaleBuild(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function staleBuildNoticeShown(): boolean {
  return noticeShown;
}

/** Test-only: reset module state between cases. */
export function resetStaleBuildForTest(): void {
  reloading = false;
  noticeShown = false;
  listeners.clear();
}
